import { useCallback, useEffect, useRef, useState } from "react";
import { LEVERAGE_STEPS } from "../constants";
import {
  LEVERAGE_LAST_INDEX,
  clientXToPosition,
  leverageToPosition,
  nearestStepIndex,
  positionToNearestStep,
} from "../utils/leverage";

/**
 * Pointer drag, track click, and keyboard handling for the leverage slider.
 *
 * @param {object} params
 * @param {number} params.value
 * @param {(value: number) => void} params.onChange
 */
export const useLeverageSlider = ({ value, onChange }) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);

  const committedPosition = leverageToPosition(value);
  const visualPosition =
    isDragging && dragPosition != null ? dragPosition : committedPosition;

  const updateFromClientX = useCallback(
    (clientX) => {
      const track = trackRef.current;

      if (!track) return;

      const position = clientXToPosition(clientX, track.getBoundingClientRect());
      const nearest = positionToNearestStep(position);

      if (nearest !== Number(value)) {
        onChange(nearest);
      }

      setDragPosition(leverageToPosition(nearest));
    },
    [onChange, value],
  );

  const focusSlider = (target) => {
    if (typeof target?.focus === "function") {
      target.focus({ preventScroll: true });
    }
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);
    focusSlider(event.currentTarget);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updateFromClientX(event.clientX);
  };

  const handlePointerMove = (event) => {
    if (!draggingRef.current) return;

    updateFromClientX(event.clientX);
  };

  const handlePointerUp = (event) => {
    if (!draggingRef.current) return;

    draggingRef.current = false;
    setIsDragging(false);

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    updateFromClientX(event.clientX);
    setDragPosition(null);
    focusSlider(event.currentTarget);
  };

  const handleKeyDown = (event) => {
    const index = nearestStepIndex(value);

    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      const next = LEVERAGE_STEPS[Math.min(LEVERAGE_LAST_INDEX, index + 1)];
      onChange(next);
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      const next = LEVERAGE_STEPS[Math.max(0, index - 1)];
      onChange(next);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      onChange(LEVERAGE_STEPS[0]);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      onChange(LEVERAGE_STEPS[LEVERAGE_LAST_INDEX]);
    }
  };

  useEffect(() => {
    if (isDragging) return;

    setDragPosition(null);
  }, [value, isDragging]);

  return {
    trackRef,
    isDragging,
    visualPosition,
    markCount: LEVERAGE_LAST_INDEX,
    steps: LEVERAGE_STEPS,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  };
};
